import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Globe } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "@/lib/i18n";

/**
 * Language selector component with flag icons and responsive design
 * Shows flags only on mobile, flags + names on desktop
 *
 * @returns {JSX.Element} Language selector dropdown
 */
function LanguageSelector() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = i18n.language || "en";
  const currentLangData =
    SUPPORTED_LANGUAGES[currentLanguage] || SUPPORTED_LANGUAGES.en;

  /**
   * Handle language change
   * @param {string} languageCode - The language code to switch to
   */
  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 px-2 sm:px-3"
          aria-label={t("language.title")}
        >
          <Globe className="h-4 w-4" />
          <span className="text-lg" role="img" aria-hidden="true">
            {currentLangData.flag}
          </span>
          <span className="hidden sm:inline-block font-medium">
            {currentLangData.name}
          </span>
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-48 p-1">
        {Object.entries(SUPPORTED_LANGUAGES).map(([code, data]) => (
          <Button
            key={code}
            variant="ghost"
            onClick={() => handleLanguageChange(code)}
            className={`w-full justify-start gap-3 h-auto py-2 px-3 ${
              code === currentLanguage ? "bg-accent" : ""
            }`}
          >
            <span
              className="text-lg"
              role="img"
              aria-label={`${data.name} flag`}
            >
              {data.flag}
            </span>
            <span className="font-medium">{data.name}</span>
            {code === currentLanguage && (
              <span className="ml-auto text-xs text-muted-foreground">
                {t("common.current", "Current")}
              </span>
            )}
          </Button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export default LanguageSelector;
